// articles/eventGuideParts.js
// Shared building blocks for the event setup guides (Captain's Choice, Best Ball, …)
// so every format guide looks, tracks and prints the same way. Split out of
// CaptainsChoiceSetup.js on Sept 27, 2026 when the second guide was written.

import React, { useEffect, useState } from 'react';
import { colors } from '../guidesData';
import { UL, LI } from '../articleUI';

// A numbered step card: the step number, a title, and its body.
export function Step({ n, title, when, children }) {
  return (
    <section
      id={`step-${n}`}
      className="guide-step"
      style={{
        border: `1px solid ${colors.gray200}`,
        borderRadius: 12,
        padding: '18px 20px 6px',
        margin: '0 0 18px',
        background: 'white',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
        <span
          style={{
            flexShrink: 0,
            width: 30,
            height: 30,
            borderRadius: 15,
            background: colors.green700,
            color: 'white',
            fontWeight: 800,
            fontSize: 15,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {n}
        </span>
        <div>
          <div style={{ fontSize: 19, fontWeight: 700, color: colors.gray900 }}>{title}</div>
          {when && <div style={{ fontSize: 14, color: colors.gray500, marginTop: 2 }}>{when}</div>}
        </div>
      </div>
      {children}
    </section>
  );
}

// An on-screen label, shown the way the organizer will see it.
export function Btn({ children }) {
  return (
    <span
      style={{
        fontWeight: 700,
        color: colors.gray900,
        background: colors.gray100,
        border: `1px solid ${colors.gray200}`,
        borderRadius: 6,
        padding: '1px 6px',
        // Wraps only when a long label won't fit a phone line.
        overflowWrap: 'anywhere',
        boxDecorationBreak: 'clone',
        WebkitBoxDecorationBreak: 'clone',
      }}
    >
      {children}
    </span>
  );
}

// "Order matters" warning inside a step.
export function Heads({ children }) {
  return (
    <div
      style={{
        background: '#fffbeb',
        border: '1px solid #fcd34d',
        borderRadius: 8,
        padding: '10px 14px',
        margin: '0 0 16px',
        fontSize: 15,
        lineHeight: 1.6,
        color: '#78350f',
      }}
    >
      {children}
    </div>
  );
}

// The step list at the top (Dennis, Sept 27): one line per step with a box to tick,
// so an organizer can keep his place while he works through the setup. Each label
// jumps to its step. Ticks are remembered in this browser only (localStorage,
// guarded — a private window just starts empty); they print as empty boxes.
// `steps`: the short step names; `storageKey`: one per guide; `note`: the line under
// the list.
export function StepTracker({ steps, storageKey, note }) {
  const [done, setDone] = useState([]);
  useEffect(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      if (Array.isArray(saved)) setDone(saved);
    } catch (e) { /* storage blocked — start empty */ }
  }, []);
  const save = (next) => {
    setDone(next);
    try { window.localStorage.setItem(storageKey, JSON.stringify(next)); } catch (e) { /* ignore */ }
  };
  const toggle = (n) => save(done.includes(n) ? done.filter((d) => d !== n) : [...done, n]);
  return (
    <div
      className="guide-tracker"
      style={{
        background: colors.green50,
        border: `1px solid ${colors.green100}`,
        borderLeft: `4px solid ${colors.green600}`,
        borderRadius: 10,
        padding: '14px 18px 12px',
        margin: '0 0 22px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
        <strong style={{ fontSize: 16, color: colors.gray900 }}>The steps, in order</strong>
        {done.length > 0 && (
          <button
            type="button"
            className="guide-no-print"
            onClick={() => save([])}
            style={{ background: 'none', border: 'none', color: colors.green700, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}
          >
            Clear ticks
          </button>
        )}
      </div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}>
        {steps.map((label, i) => {
          const n = i + 1;
          const isDone = done.includes(n);
          return (
            <li key={n} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 16, lineHeight: 1.5 }}>
              <input
                type="checkbox"
                id={`track-${n}`}
                checked={isDone}
                onChange={() => toggle(n)}
                aria-label={`Step ${n} done`}
                style={{ width: 18, height: 18, accentColor: colors.green700, flexShrink: 0, cursor: 'pointer' }}
              />
              <a
                href={`#step-${n}`}
                style={{
                  color: isDone ? colors.gray400 : colors.gray800,
                  textDecoration: isDone ? 'line-through' : 'none',
                  fontWeight: 600,
                }}
              >
                {n}. {label}
              </a>
            </li>
          );
        })}
      </ol>
      {note && <div style={{ fontSize: 14, color: colors.gray600, marginTop: 8 }}>{note}</div>}
    </div>
  );
}

// Printing (Dennis, Sept 27): organizers print the guide and work through it beside
// the screen. Print only the article: no site header, footer, back link or CTA, and
// keep each step on one page where it fits.
export const PRINT_CSS = `
@media print {
  header, footer, nav, .guide-no-print { display: none !important; }
  main { padding: 0 !important; max-width: none !important; }
  body, main, article { background: white !important; }
  .guide-step { break-inside: avoid; page-break-inside: avoid; }
  .guide-shot { break-inside: avoid; page-break-inside: avoid; max-width: 190px !important; }
  h2, h3 { break-after: avoid; page-break-after: avoid; }
  @page { margin: 0.6in; }
}
`;

// A screenshot of the player app inside a simple phone outline (Sept 27, 2026).
// `src` is a path under frontend/public (e.g. /guides/scoring/card.webp); the
// images are real screens rendered from the app's own code with sample players.
export function PhoneShot({ src, alt, caption, width = 250 }) {
  return (
    <figure className="guide-shot" style={{ margin: '4px auto 22px', maxWidth: width, textAlign: 'center' }}>
      <div
        style={{
          background: '#111827',
          borderRadius: 34,
          padding: '12px 9px',
          boxShadow: '0 10px 30px rgba(17, 24, 39, 0.18)',
        }}
      >
        <img
          src={src}
          alt={alt}
          loading="lazy"
          style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 24, background: 'white' }}
        />
      </div>
      {caption && (
        <figcaption style={{ fontSize: 14, lineHeight: 1.5, color: colors.gray500, marginTop: 10 }}>
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

// Two or more PhoneShots side by side; they stack on a narrow screen.
export function PhoneRow({ children }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'center', margin: '4px 0 6px' }}>
      {children}
    </div>
  );
}


// Closest to the Pin, added alongside the main game (Sept 28, 2026). One section shared
// by every format guide, shown at the end of the game step. `team`: the Captain's
// Choice wording (one team card, any teammate records it). Quotes the CTP panel's
// labels (CtpGamePanel, CtpClaimBar in App.js) — change here if they change there.
export function CtpSection({ team = false }) {
  return (
    <div
      id="closest-to-the-pin"
      style={{
        background: colors.green50,
        border: `1px solid ${colors.green100}`,
        borderRadius: 10,
        padding: '14px 18px 4px',
        margin: '6px 0 16px',
      }}
    >
      <div style={{ fontSize: 17, fontWeight: 700, color: colors.gray900, marginBottom: 4 }}>
        Optional: add Closest to the Pin
      </div>
      <div style={{ fontSize: 15, lineHeight: 1.6, color: colors.gray700, marginBottom: 8 }}>
        Closest to the Pin (CTP) runs alongside the main game, on the par 3s, with no handicaps.
        TTO doesn&rsquo;t measure anything: players mark the ball on the green as usual, and TTO
        records who has it.
      </div>
      <UL>
        <LI>
          In <Btn>🎮 Games</Btn>, also tick <strong>Closest to the Pin</strong>. (Not in your list?
          Games come with the Plus plan and above, and with an Event Pass.)
        </LI>
        <LI>
          <strong>Holes:</strong> TTO lists the course&rsquo;s par 3s, each set to{' '}
          <Btn>Include</Btn>. Switch any you don&rsquo;t want to <Btn>Exclude</Btn>. Playing it on
          a hole that isn&rsquo;t a par 3? Type it under <strong>Also on another hole</strong>.
        </LI>
        <LI>
          <strong>Must make par</strong> (optional): the question on the scoring screen becomes
          &ldquo;On the green, closer, and made par?&rdquo; A closer player who misses his par
          doesn&rsquo;t record it, so the hole stays with the last player who did.
        </LI>
        <LI>
          <strong>The wager:</strong> most events leave it at <strong>$0</strong>. TTO names each
          hole&rsquo;s winner and you pay from your prize table. To settle it in TTO instead,
          choose <Btn>A set prize per hole</Btn> (for example $20 a hole, paid equally by the
          players in the game) or <Btn>Each player puts in, per hole</Btn> (for example $1 a player,
          so with 40 players a hole is worth $40). A hole nobody hits pays nothing.
        </LI>
        <LI>
          Click <Btn>Save Games</Btn>, then put the players in: in <strong>Player Opt-Ins</strong>{' '}
          under Closest to the Pin, tap each player to <strong>✓ In</strong>. Players who use their
          own app link can also opt themselves in. Only players who are in can win it.
        </LI>
        <LI>
          <strong>On the course:</strong> on a CTP hole the scoring screen shows{' '}
          <strong>📍 Closest to the Pin</strong> and who has it. A player on the green who is closer
          taps <Btn>I have it</Btn> (or {team ? 'a teammate' : 'a playing partner'}&rsquo;s name),
          then <Btn>Yes, record it</Btn>. A closer player later takes it over.{' '}
          {team
            ? 'On the team card any teammate can record it, not only the player keeping the card.'
            : 'It works the same for players who scanned the QR on their card.'}
        </LI>
        <LI>
          <strong>Not scoring live, or a volunteer at the hole?</strong> On the day of the round,{' '}
          <Btn>🎮 Games</Btn> &rarr; Closest to the Pin shows <strong>Who has it</strong>: pick the
          player for each hole, or clear it.
        </LI>
        <LI>
          <strong>Where it shows:</strong> a 📍 Closest to the Pin list under the leaderboard (the
          hole and the name, or &ldquo;&mdash;&rdquo; until someone has it), a line along the bottom
          of the TV board, and the winners on the statement when you finalize.
          {team ? ' With money on it, CTP pays once every team has played all the CTP holes.' : ''}
        </LI>
      </UL>
    </div>
  );
}
